import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Anchor, Tablet, User, KeyRound } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatErr } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { getGuest, signInGuestWithSession, setIpadSession, isIpadMode } from "@/lib/guest";
import { prefetchGuestKiosk, prefetchGuestKioskWithProgress, prefetchImage } from "@/lib/prefetch";
import { useAuth } from "@/context/AuthContext";

const RESUME_PARAM = "resume";

export default function GuestLanding() {
  const navigate = useNavigate();
  const { boatName, spaName, loginLogoUrl: logoUrl, heroUrl } = useBranding();
  const { login: crewLogin } = useAuth();
  const [cabins, setCabins] = useState([]);
  const [mode, setMode] = useState("guest"); // 'guest' | 'ipad' | 'crew'
  const [cabinId, setCabinId] = useState("");
  const [pin, setPin] = useState("");
  // Crew tab — email / password. Only visible when this device is NOT a
  // shared cabin iPad; the kiosk must never expose an admin sign-in path.
  const [crewEmail, setCrewEmail] = useState("");
  const [crewPassword, setCrewPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [rosterPicker, setRosterPicker] = useState(null); // {cabin_id, cabin_name, charter_id, cabin_roster, resume_token?}
  const [resuming, setResuming] = useState(false);
  // iPad kiosk prefetch state — while non-null, a full-screen overlay
  // shows a "Preparing your cabin experience" progress bar and the
  // route to /cabin is blocked until every treatment photo, crew
  // avatar and the cabin cover has been fetched into memory cache +
  // IndexedDB. This is the piece that eliminates the "tiles loading
  // one-by-one" flicker on the shared iPad.
  const [kioskProgress, setKioskProgress] = useState(null);
  // ^ { cabin_name, step, phase, loaded, total }
  const showCrewTab = !isIpadMode();

  useEffect(() => {
    // 1-tap resume: iPad-minted QR encodes ?resume=<jwt>. Redeem → show a
    // "Who is this?" picker. Tapping a name signs the mobile in as THAT
    // personal guest — the iPad session is never touched.
    const url = new URL(window.location.href);
    const token = url.searchParams.get(RESUME_PARAM);
    if (token) {
      setResuming(true);
      (async () => {
        try {
          const { data } = await api.post("/public/mobile/resume", { token });
          // Clean the URL so a refresh doesn't attempt to re-consume the token.
          url.searchParams.delete(RESUME_PARAM);
          window.history.replaceState({}, "", url.toString());
          // Personal-QR fast path: token locked to a single guest → skip the
          // roster picker entirely and auto-sign that guest in on this phone.
          if (data.preselect_guest_id) {
            try {
              const { data: session } = await api.post("/public/mobile/pick", { token, guest_id: data.preselect_guest_id });
              signInGuestWithSession(session);
              toast.success(`Welcome, ${session.guest_name}`);
              navigate("/experience", { replace: true });
              return;
            } catch (err) {
              toast.error(formatErr(err.response?.data?.detail) || "Auto sign-in failed — pick your name below.");
            }
          }
          setRosterPicker({ ...data, resume_token: token, from_qr: true });
          setResuming(false);
        } catch (err) {
          toast.error(formatErr(err.response?.data?.detail) || "This QR expired — ask the iPad to show a fresh one.");
          setResuming(false);
        }
      })();
      return;
    }
    const g = getGuest();
    // iPad mode with a stored session token → land on cabin common view.
    if (g.ipad_mode && g.ipad_token && g.cabin_id) { navigate("/cabin", { replace: true }); return; }
    if (g.name && g.id) { navigate("/experience", { replace: true }); return; }
    (async () => {
      try {
        const { data } = await api.get("/public/active-cabins");
        setCabins(data || []);
      } catch { setCabins([]); }
      finally { setLoading(false); }
    })();
  }, [navigate]);

  const hasActiveTrip = cabins.length > 0;

  const submitGuestPin = async (e) => {
    e.preventDefault();
    if (!cabinId || !pin) return;
    if (!/^\d{4}$/.test(pin)) { toast.error("PIN must be 4 digits"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/guest-login", { cabin_id: cabinId, pin });
      signInGuestWithSession(data);
      toast.success(`Welcome, ${data.guest_name}`);
      navigate("/experience");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Invalid cabin or PIN");
    } finally { setSubmitting(false); }
  };

  const submitIpad = async (e) => {
    e.preventDefault();
    if (!cabinId || !pin) return;
    if (!/^\d{4}$/.test(pin)) { toast.error("PIN must be 4 digits"); return; }
    setSubmitting(true);
    try {
      // Persistent device_id — lets the backend keep per-device iPad sessions
      // stable across reconnects and gives the Connected Devices panel a
      // stable identifier to display. Generated once per browser and reused.
      let deviceId = localStorage.getItem("yacht_ipad_device_id");
      if (!deviceId) {
        deviceId = (crypto?.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36)).replace(/-/g, "").slice(0, 16);
        try { localStorage.setItem("yacht_ipad_device_id", deviceId); } catch { /* ignore private mode */ }
      }
      const { data } = await api.post("/public/ipad-login", { cabin_id: cabinId, ipad_pin: pin, device_id: deviceId });
      // Store the long-lived iPad session then land on the shared cabin view (no roster pick needed).
      setIpadSession(data.cabin_id, data.cabin_name, data.charter_id, data.cabin_roster, data.session_token, data.charter_arrival, data.charter_departure, data.cabin_cover_image_path);
      // Show the prefetch overlay AND await the full warm-up before
      // navigating. Cover, treatment tiles and crew avatars all land
      // in memory-cache + IDB so the cabin dashboard paints instantly
      // and the menu photos are already resident on first tap.
      const extra = [];
      if (data.cabin_cover_image_path) extra.push(data.cabin_cover_image_path);
      // Guest avatars from the roster so the "Who's booking?" chip
      // grid on CabinView paints from cache too.
      for (const g of (data.cabin_roster || [])) {
        if (g && g.image_path) extra.push(g.image_path);
      }
      setKioskProgress({
        cabin_name: data.cabin_name || "your cabin",
        step: "Connecting to spa…",
        phase: "data", loaded: 0, total: 1,
      });
      // P0 boot-speed fix: cap the overlay at 5 s (JSON + session only —
      // images warm in the background now via `blockOnImages: false`).
      // Previously the overlay awaited every photo with a 20 s ceiling,
      // which held the "Preparing your cabin" screen for the full budget
      // on flaky yacht WiFi. Images stream into IDB after navigation and
      // BlobImage races cache-vs-direct so tiles still paint the moment
      // the guest reaches /menu.
      await Promise.race([
        prefetchGuestKioskWithProgress({
          extraImagePaths: extra,
          ipadCredentials: { cabin_id: data.cabin_id, session_token: data.session_token },
          blockOnImages: false,
          onProgress: (p) => setKioskProgress((prev) => prev ? { ...prev, ...p } : prev),
          onStep: (step) => setKioskProgress((prev) => prev ? { ...prev, step } : prev),
        }),
        new Promise((resolve) => setTimeout(resolve, 5_000)),
      ]);
      // Tiny hold so the "100%" bar is visible for one animation frame
      // before the route swap; feels more polished than an instant cut.
      setKioskProgress((prev) => prev ? { ...prev, step: "Ready — welcome aboard", loaded: prev.total || 0, total: prev.total || 0 } : prev);
      await new Promise((r) => setTimeout(r, 350));
      navigate("/cabin");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Invalid cabin or iPad PIN");
    } finally { setSubmitting(false); }
  };

  const pickGuestFromRoster = async (g) => {
    if (!rosterPicker) return;
    // Mobile-QR resume flow: exchange the token for a stamped personal session.
    if (rosterPicker.resume_token) {
      try {
        const { data } = await api.post("/public/mobile/pick", {
          token: rosterPicker.resume_token, guest_id: g.guest_id,
        });
        signInGuestWithSession(data);
        setRosterPicker(null);
        toast.success(`Welcome, ${data.guest_name}`);
        navigate("/experience");
      } catch (err) {
        toast.error(formatErr(err.response?.data?.detail) || "Please try again from the iPad.");
      }
      return;
    }
    signInGuestWithSession({
      guest_id: g.guest_id,
      guest_name: g.guest_name,
      cabin_id: rosterPicker.cabin_id,
      cabin_name: rosterPicker.cabin_name,
      charter_id: rosterPicker.charter_id,
      cabin_roster: rosterPicker.cabin_roster,
      female: g.female,
      partner_guest_id: g.partner_guest_id,
    });
    navigate("/experience");
  };

  const submitCrew = async (e) => {
    e.preventDefault();
    if (!crewEmail || !crewPassword) return;
    setSubmitting(true);
    try {
      const user = await crewLogin(crewEmail, crewPassword);
      toast.success("Welcome back");
      // Spa Ops kiosk accounts land on the dedicated kiosk; everyone
      // else goes to the admin dashboard.
      if (user?.role === "spa_ops" || user?.role === "pantry") navigate("/spa-ops");
      else navigate("/admin");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Login failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="relative min-h-screen bg-background">
      <div className="absolute inset-0 h-[100vh] overflow-hidden bg-primary">
        {heroUrl && <img src={heroUrl} alt="Yacht" className="w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-primary/40" />
      </div>

      <div className="relative z-10 min-h-screen flex flex-col">
        <div className="px-6 md:px-14 py-6 flex items-center justify-between text-background">
          <div className="flex items-center gap-3">
            {logoUrl ? <img src={logoUrl} alt="Logo" className="h-8 w-8 object-contain" /> : <Anchor className="h-5 w-5" strokeWidth={1.5} />}
            <span className="font-display text-lg tracking-tight">{spaName}</span>
          </div>
          {/* No "Crew Access" link here — this landing page is dedicated
              to guests and the shared cabin iPad. Crew reach /admin/login
              directly via bookmark, not through the guest surface. */}
        </div>

        <div className="flex-1 grid lg:grid-cols-12 gap-10 items-center px-6 md:px-14 py-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: "easeOut" }} className="lg:col-span-7 text-background">
            <div className="overline text-accent mb-8">On-Board Wellness</div>
            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[1.02] tracking-tight mb-8" style={{ textShadow: "0 4px 30px rgba(0,0,0,0.35)" }}>
              A quiet<br />ocean of<br /><em className="text-accent font-normal">calm.</em>
            </h1>
            <p className="text-lg md:text-xl max-w-xl text-background/85 font-light leading-relaxed">
              Signature spa rituals, hand-crafted by our onboard therapists.
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.2, ease: "easeOut" }} className="lg:col-span-5">
            <div className="bg-card/95 backdrop-blur-md p-10 md:p-12 rounded-none border border-border shadow-2xl">
              {loading ? (
                <div className="text-muted-foreground overline">Preparing your welcome…</div>
              ) : (
                <>
                  {/* Tabs — always rendered so crew can sign in even when
                      no charter is active (e.g. between trips, when they
                      need to update settings, prep the roster, or view
                      past logs). The Cabin iPad tab still requires an
                      active trip because it's a PIN-based flow. */}
                  <div className="flex gap-2 mb-8 border-b border-border" data-testid="login-mode-tabs">
                    <button
                      onClick={() => { setMode("guest"); setPin(""); }}
                      className={`pb-3 px-2 text-sm inline-flex items-center gap-2 transition-colors ${mode === "guest" ? "text-primary border-b-2 border-primary" : "text-muted-foreground hover:text-primary"}`}
                      data-testid="mode-guest"
                    >
                      <User className="h-4 w-4" /> Guest
                    </button>
                    {hasActiveTrip && (
                      <button
                        onClick={() => { setMode("ipad"); setPin(""); }}
                        className={`pb-3 px-2 text-sm inline-flex items-center gap-2 transition-colors ${mode === "ipad" ? "text-primary border-b-2 border-primary" : "text-muted-foreground hover:text-primary"}`}
                        data-testid="mode-ipad"
                      >
                        <Tablet className="h-4 w-4" /> Cabin iPad
                      </button>
                    )}
                    {/* Crew tab is hidden on the shared cabin iPad — a
                        curious guest must never see an admin sign-in
                        surface on that device. It shows on personal
                        phones, laptops and un-provisioned browsers —
                        regardless of whether a charter is currently
                        active, so the crew can always reach the portal. */}
                    {showCrewTab && (
                      <button
                        onClick={() => { setMode("crew"); setPin(""); }}
                        className={`pb-3 px-2 text-sm inline-flex items-center gap-2 transition-colors ${mode === "crew" ? "text-primary border-b-2 border-primary" : "text-muted-foreground hover:text-primary"}`}
                        data-testid="mode-crew"
                      >
                        <KeyRound className="h-4 w-4" /> Crew
                      </button>
                    )}
                  </div>

                  {/* Guest tab body — swaps between the PIN sign-in (when
                      a charter is active) and the classic name-only form
                      (when there's no active trip so guests can still
                      browse the menu on a quiet week). */}
                  {mode === "guest" && hasActiveTrip && (
                    <>
                      <div className="overline text-primary/60 mb-4">Guest Sign-in</div>
                      <h2 className="font-display text-3xl mb-2">Your cabin</h2>
                      <p className="text-sm text-muted-foreground mb-8">Enter your personal 4-digit PIN.</p>
                      <form onSubmit={submitGuestPin} className="space-y-6" data-testid="cabin-login-form">
                        <div>
                          <label className="overline text-primary/60 text-[10px] block mb-2">Cabin</label>
                          <Select value={cabinId} onValueChange={setCabinId}>
                            <SelectTrigger className="h-14 border-0 border-b-2 border-primary/20 rounded-none px-0 focus:ring-0 focus:border-primary text-lg justify-between" data-testid="cabin-select">
                              <SelectValue placeholder="Select cabin" />
                            </SelectTrigger>
                            <SelectContent>
                              {cabins.map((c) => <SelectItem key={c.cabin_id} value={c.cabin_id}>{c.cabin_name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="overline text-primary/60 text-[10px] block mb-2">4-digit PIN</label>
                          <Input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                            inputMode="numeric" maxLength={4} placeholder="••••" data-testid="pin-input"
                            className="bg-transparent border-0 border-b-2 border-primary/20 rounded-none px-0 text-2xl h-14 focus-visible:ring-0 focus-visible:border-primary font-mono tracking-[0.5em]" />
                        </div>
                        <Button type="submit" disabled={!cabinId || pin.length !== 4 || submitting} data-testid="cabin-login-submit"
                          className="w-full h-14 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-normal text-base">
                          {submitting ? "Signing in…" : "Enter"} <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </form>
                    </>
                  )}
                  {mode === "ipad" && (
                    <>
                      <div className="overline text-primary/60 mb-4">Cabin iPad</div>
                      <h2 className="font-display text-3xl mb-2">Public sign-in</h2>
                      <p className="text-sm text-muted-foreground mb-8">Enter the cabin's shared iPad PIN.</p>
                      <form onSubmit={submitIpad} className="space-y-6" data-testid="ipad-login-form">
                        <div>
                          <label className="overline text-primary/60 text-[10px] block mb-2">Cabin</label>
                          <Select value={cabinId} onValueChange={setCabinId}>
                            <SelectTrigger className="h-14 border-0 border-b-2 border-primary/20 rounded-none px-0 focus:ring-0 focus:border-primary text-lg justify-between" data-testid="ipad-cabin-select">
                              <SelectValue placeholder="Select cabin" />
                            </SelectTrigger>
                            <SelectContent>
                              {cabins.map((c) => <SelectItem key={c.cabin_id} value={c.cabin_id}>{c.cabin_name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="overline text-primary/60 text-[10px] block mb-2">iPad PIN</label>
                          <Input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                            inputMode="numeric" maxLength={4} placeholder="••••" data-testid="ipad-pin-input"
                            className="bg-transparent border-0 border-b-2 border-primary/20 rounded-none px-0 text-2xl h-14 focus-visible:ring-0 focus-visible:border-primary font-mono tracking-[0.5em]" />
                        </div>
                        <Button type="submit" disabled={!cabinId || pin.length !== 4 || submitting} data-testid="ipad-login-submit"
                          className="w-full h-14 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-normal text-base">
                          {submitting ? "Signing in…" : "Public login"} <Tablet className="ml-2 h-4 w-4" />
                        </Button>
                      </form>
                    </>
                  )}
                  {/* Crew tab body — only reachable when `showCrewTab` is true. */}
                  {mode === "crew" && (
                    <>
                      <div className="overline text-primary/60 mb-4">Crew Portal</div>
                      <h2 className="font-display text-3xl mb-2">Crew sign-in</h2>
                      <p className="text-sm text-muted-foreground mb-8">Manage the spa, the crew, the calm.</p>
                      <form onSubmit={submitCrew} className="space-y-6" data-testid="crew-login-form">
                        <div>
                          <label className="overline text-primary/60 text-[10px] block mb-2">Email</label>
                          <Input
                            type="email"
                            autoComplete="email"
                            value={crewEmail}
                            onChange={(e) => setCrewEmail(e.target.value)}
                            data-testid="crew-email-input"
                            placeholder="you@yacht.example"
                            className="bg-transparent border-0 border-b-2 border-primary/20 rounded-none px-0 text-lg h-14 focus-visible:ring-0 focus-visible:border-primary"
                          />
                        </div>
                        <div>
                          <label className="overline text-primary/60 text-[10px] block mb-2">Password</label>
                          <Input
                            type="password"
                            autoComplete="current-password"
                            value={crewPassword}
                            onChange={(e) => setCrewPassword(e.target.value)}
                            data-testid="crew-password-input"
                            placeholder="••••••••"
                            className="bg-transparent border-0 border-b-2 border-primary/20 rounded-none px-0 text-lg h-14 focus-visible:ring-0 focus-visible:border-primary"
                          />
                        </div>
                        <Button
                          type="submit"
                          disabled={!crewEmail || !crewPassword || submitting}
                          data-testid="crew-login-submit"
                          className="w-full h-14 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-normal text-base"
                        >
                          {submitting ? "Signing in…" : "Sign in"} <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </form>
                    </>
                  )}
                </>
              )}
              {/* No-active-trip fallback for the Guest tab — a short-name
                  form so a curious visitor can still peek at the menu.
                  The Crew tab above handles the between-trips crew flow. */}
              {!loading && !hasActiveTrip && mode === "guest" && (
                <>
                  <div className="overline text-primary/60 mb-4">Welcome Aboard</div>
                  <h2 className="font-display text-3xl mb-2">Your journey begins soon</h2>
                  <p className="text-sm text-muted-foreground mb-8">Please ask the crew for your guest access link or cabin PIN when your journey is ready.</p>
                </>
              )}
            </div>
          </motion.div>
        </div>

        <div className="px-6 md:px-14 py-8 text-background/60 text-xs overline">
          <span>Est. 2026 · Onboard {boatName}</span>
        </div>
      </div>

      {/* iPad prefetch overlay — full-screen while a freshly-authenticated
          iPad warms every asset it needs for the cabin dashboard, so the
          crew see actual progress instead of a "loading treatments" flash
          later. Un-dismissable — resolves itself when prefetch finishes. */}
      {kioskProgress && (() => {
        // Prefer the unified overall percentage published by
        // `prefetchGuestKioskWithProgress`. Falls back to a phase-
        // scoped calculation for backwards-compat if someone renders
        // this from another caller.
        const pct = typeof kioskProgress.pct === "number"
          ? kioskProgress.pct
          : (kioskProgress.total > 0
              ? Math.min(100, Math.round((kioskProgress.loaded / kioskProgress.total) * 100))
              : (kioskProgress.step?.startsWith("Ready") ? 100 : 5));
        return (
          <div
            className="fixed inset-0 z-[70] bg-primary/98 text-background flex items-center justify-center p-8 backdrop-blur"
            data-testid="kiosk-prefetch-overlay"
          >
            <div className="w-full max-w-lg text-center">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
                className="mb-8"
              >
                {logoUrl ? (
                  <img src={logoUrl} alt="" className="h-14 mx-auto mb-6 opacity-95" />
                ) : (
                  <Anchor className="h-10 w-10 mx-auto mb-6 text-accent" strokeWidth={1.4} />
                )}
                <div className="overline text-accent mb-4">Preparing your cabin</div>
                <h2 className="font-display text-3xl sm:text-4xl mb-3 leading-tight">
                  Welcome to {kioskProgress.cabin_name}
                </h2>
                <p className="text-background/70 text-sm max-w-sm mx-auto">
                  {kioskProgress.step || "Getting things ready…"}
                </p>
              </motion.div>

              {/* Progress bar — always-visible so the crew know something
                  is happening even when there are zero photos to warm. */}
              <div
                className="h-1.5 bg-background/15 rounded-full overflow-hidden"
                role="progressbar"
                aria-valuenow={pct}
                aria-valuemin={0}
                aria-valuemax={100}
                data-testid="kiosk-prefetch-bar"
              >
                <motion.div
                  className="h-full bg-accent"
                  initial={{ width: 0 }}
                  animate={{ width: `${pct}%` }}
                  transition={{ duration: 0.35, ease: "easeOut" }}
                />
              </div>
              <div className="mt-3 text-[11px] uppercase tracking-widest text-background/50 flex items-center justify-between">
                <span data-testid="kiosk-prefetch-phase">
                  {kioskProgress.step?.startsWith("Ready") ? "Complete" : (kioskProgress.phase === "images" ? "Photos" : "Menu · Crew · Settings")}
                </span>
                <span data-testid="kiosk-prefetch-pct">{pct}%</span>
              </div>
              {typeof kioskProgress.loaded === "number" && kioskProgress.total > 0 && (
                <div className="mt-1 text-[10px] text-background/40 text-right">
                  {kioskProgress.loaded} of {kioskProgress.total} assets ready
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Roster picker (iPad flow) */}
      <Dialog open={!!rosterPicker} onOpenChange={(v) => !v && setRosterPicker(null)}>
        <DialogContent data-testid="roster-picker">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {rosterPicker?.resume_token ? "Who's connecting?" : "Who's booking?"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mb-4">
            {rosterPicker?.resume_token
              ? `Tap your name to sign in on this phone. ${rosterPicker?.cabin_name || ""} · your iPad stays connected.`
              : "Select your name to continue on this cabin's iPad."}
          </p>
          <div className="space-y-2">
            {(rosterPicker?.cabin_roster || []).map((g) => (
              <button
                key={g.guest_id}
                onClick={() => pickGuestFromRoster(g)}
                className="w-full text-left px-5 py-4 bg-card border border-border hover:border-primary transition-colors flex items-center gap-3"
                data-testid={`roster-pick-${g.guest_id}`}
              >
                <User className="h-4 w-4 text-primary/60" />
                <span className="font-display text-lg">{g.guest_name}</span>
                {g.female && <span className="overline text-accent text-[10px]">♀</span>}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
